import { generateApiRoute } from '@klp/core/api/helpers';
import { baseApi } from '@klp/core/api';
import type {
    COUNTRY_WITH_API_SUPPORT,
    Foretak,
    LeverandorGodkjenningProsessResponse,
    LeverandorStatistics,
    Leverandorvurdering,
    RisikoVurderingStatus,
} from '../types';
import type { Bruker } from '@klp/user/index';
import { brukerApiName } from '@klp/user/config';
import type { FormField } from '@klp/formgenerator/interfaces';

const leverandorApiName = 'leverandor';
const leverandorProcessApiName = 'leverandor/process';
const executeTask = 'utforoppgave';
const updateForm = 'updateform';
const processinstansSegment = 'processinstans';
const processDefinitionSegment = 'prosessdefinisjon';
const leverandorStatus = 'leverandorregistreringstatus';
const vurderingSegment = 'vurdering';
const vurderingListSegment = 'vurderinglist';
const approvedDocumentSegment = 'approveddocument';
const fileSegment = 'fil';
const vedleggSegment = 'vedlegg';
const statistikkSegment = 'statistikk';
const xmlSegment = 'xml';
const sendReminderSegment = 'sendreminder';
const assignProcessSegment = 'tildelprosess';
const confluenceSegment = 'confluence';
const typeSegment = 'type';
const notifyRoleWithCommentSegment = 'notifyrolewithcomment';
const content = 'content';
const page = 'page';
const config = 'config';
const role = 'rolle';
const company = 'company';
const search = 'search';
const superoffice = 'superoffice';
const bransje = 'bransje';
const risikoApiName = 'risiko';
const kodeverk = 'kodeverk';
const land = 'land';

// RisikoVurdering API config
const kdiApiName = 'kdi';
const risikoVurderingApiName = 'risikovurdering';

export type AssignLeverandorProcessRequest = {
    superOfficeContactId?: string;
    ansvarlig: string;
    formFields: Array<FormField>;
    prosessInstansId: string;
};

export type CreateLeverandorProcessRequest = {
    virksomhet: CompanySimpleDto;
    region: string;
    superOfficeContactId?: string;
    culture?: string;
    type?: string;
};

type CompanySimpleDto = {
    organisasjonsnummer: string;
    land: string;
    navn?: string;
    SOCountryId?: string;
};

type GetLeverandorProcessRequest = {
    portfolio?: string;
    region?: string;
    orgnummer?: string;
    fromDate?: string;
    statuses?: Array<string>;
    culture?: string;
    // Partial match on leverandørnummer, org.nummer or org.navn
    sok?: string;
    // E-mail of a user. Gives every process with an active task assigned to
    // the user, and the other filters aren't used.
    tildeltTil?: string;
};

export type UtforOppgave = {
    oppgaveId: string;
    registreringStatus?: string; //enum
    formFields?: Array<FormField>;
    autoAssign?: boolean;
};

type SaveLeverandorProcessFormfieldsRequest = Pick<
    UtforOppgave,
    'oppgaveId' | 'formFields'
>;

export type DynamicFormData = {
    id: string;
    value?: string;
};

export type ConfluenceContent = {
    id?: string;
    body?: string;
    styling?: string;
    readLink?: string;
    editLink?: string;
};

type VedleggRequest = {
    name: string;
    type?: string;
    content?: string;
    contentStream?: {
        capacity: number;
        position: number;
        readTimeout: number;
        writeTimeout: number;
    };
};

export type VedleggResponse = {
    id: number;
    prosessInstansId: string;
    name: string;
    type: string;
    superOfficeDocumentId: string;
};

export type CompanySearchResponse = {
    organisasjonsnummer: string;
    navn: string | null;
    organisasjonsform: string | null;
    aktiv: boolean | null;
    registreringsdato: string | null;
    naeringskode: string | null;
    naeringsbeskrivelse: string | null;
    adresse: string | null;
    postnummer: string | null;
    poststed: string | null;
    land: string | null;
    hjemmeside: string | null;
    telefon: string | null;
    kilde: string | null;
};

type ForetakRequest = {
    orgNumber: string;
    countryCode: string;
};

type ForetakResponse = {
    status: RisikoVurderingStatus;
    data: Foretak;
    success: boolean;
    timestamp: string;
    processingTime: number;
};

type LeverandorInfo = Leverandorvurdering;

export const leverandorApi = baseApi.injectEndpoints({
    endpoints: (build) => ({
        getLeverandorStatus: build.query<Array<string>, void>({
            query: () => ({
                url: generateApiRoute([
                    leverandorProcessApiName,
                    leverandorStatus,
                ]),
            }),
            transformResponse: (response: Record<string, number>) =>
                Object.keys(response),
        }),
        getLeverandorRoles: build.query<Array<string>, void>({
            query: () => ({
                url: generateApiRoute([leverandorProcessApiName, role]),
            }),
        }),
        getLeverandorTypes: build.query<Array<string>, void>({
            query: () => ({
                url: generateApiRoute([leverandorProcessApiName, typeSegment]),
            }),
        }),
        getUsersByLeverandorRoleAndRegion: build.query<
            Array<Bruker>,
            { rolle: string; region: string | undefined }
        >({
            query: ({ rolle, region }) => ({
                url: generateApiRoute([brukerApiName]),
                params: { rolle, region },
            }),
        }),
        getLeverandorVurdering: build.query<
            Leverandorvurdering,
            { orgnummer?: string }
        >({
            query: ({ orgnummer }) => ({
                url: generateApiRoute([leverandorApiName, vurderingSegment]),
                params: { orgnummer },
            }),
            providesTags: ['LeverandorVurdering'],
        }),
        getLeverandorVurderingList: build.query<
            Array<Leverandorvurdering>,
            { orgnummer: string }
        >({
            query: ({ orgnummer }) => ({
                url: generateApiRoute([
                    leverandorApiName,
                    vurderingListSegment,
                ]),
                params: { orgnummer },
            }),
            providesTags: ['LeverandorVurdering'],
        }),
        getLeverandorProcessList: build.query<
            Array<LeverandorGodkjenningProsessResponse>,
            GetLeverandorProcessRequest
        >({
            query: (args) => {
                return {
                    url: generateApiRoute([leverandorProcessApiName]),
                    params: { ...args },
                };
            },
            providesTags: ['LeverandorVurderingProcess'],
        }),

        getLeverandorProcessByContactId: build.query<
            LeverandorGodkjenningProsessResponse,
            { superOfficeContactId: string; culture?: string }
        >({
            query: ({ superOfficeContactId, culture }) => ({
                url: generateApiRoute([
                    leverandorProcessApiName,
                    superOfficeContactId,
                ]),
                params: { culture },
            }),
            providesTags: ['LeverandorVurderingProcess'],
        }),

        getLeverandorProcessByProcessId: build.query<
            LeverandorGodkjenningProsessResponse,
            { prosessInstansId: string; culture?: string }
        >({
            query: ({ prosessInstansId, culture }) => ({
                url: generateApiRoute([
                    leverandorProcessApiName,
                    processinstansSegment,
                    prosessInstansId,
                ]),
                params: { culture },
            }),
            providesTags: ['LeverandorVurderingProcess'],
        }),

        createLeverandorProcess: build.mutation<
            LeverandorGodkjenningProsessResponse,
            CreateLeverandorProcessRequest
        >({
            query: (request) => ({
                url: generateApiRoute([leverandorProcessApiName]),
                method: 'POST',
                body: request,
            }),
            invalidatesTags: ['LeverandorVurderingProcess'],
        }),
        deleteLeverandorProcess: build.mutation<
            void,
            { prosessInstansId: string }
        >({
            query: ({ prosessInstansId }) => ({
                url: generateApiRoute([
                    leverandorProcessApiName,
                    prosessInstansId,
                ]),
                method: 'DELETE',
            }),
            invalidatesTags: [
                'LeverandorVurderingProcess',
                'SuperOfficeProsjekt',
            ],
        }),
        executeLeverandorTask: build.mutation<
            LeverandorGodkjenningProsessResponse,
            UtforOppgave
        >({
            query: (request) => ({
                url: generateApiRoute([leverandorProcessApiName, executeTask]),
                method: 'POST',
                body: request,
            }),
            invalidatesTags: ['LeverandorVurderingProcess'],
        }),
        saveLeverandorProcessFormfields: build.mutation<
            void,
            SaveLeverandorProcessFormfieldsRequest
        >({
            query: (body) => ({
                url: generateApiRoute([leverandorProcessApiName, updateForm]),
                method: 'PATCH',
                body,
            }),
        }),
        getLeverandorProcessDefinitionXml: build.query<
            string,
            { processDefinitionId: string }
        >({
            query: ({ processDefinitionId }) => ({
                url: generateApiRoute([
                    leverandorProcessApiName,
                    processDefinitionSegment,
                    processDefinitionId,
                    xmlSegment,
                ]),
                responseHandler: 'text',
            }),
        }),
        getLeverandorConfluenceContent: build.query<
            ConfluenceContent,
            { id: string }
        >({
            query: ({ id }) => ({
                url: generateApiRoute([
                    config,
                    confluenceSegment,
                    content,
                    page,
                    id,
                ]),
            }),
        }),
        sendLeverandorReminder: build.mutation<
            void,
            { prosessInstansId: string }
        >({
            query({ prosessInstansId }) {
                return {
                    url: generateApiRoute([
                        leverandorProcessApiName,
                        sendReminderSegment,
                    ]),
                    method: 'POST',
                    params: { prosessInstansId },
                };
            },
        }),
        sendNotificationWithComment: build.mutation<
            void,
            {
                prosessInstansId: string;
                rolle: string;
                comment: string;
                rolleKommentarer?: string;
            }
        >({
            query({ prosessInstansId, rolle, comment, rolleKommentarer }) {
                return {
                    url: generateApiRoute([
                        leverandorProcessApiName,
                        notifyRoleWithCommentSegment,
                    ]),
                    method: 'POST',
                    params: { prosessInstansId, rolle },
                    body: { comment, rolleKommentarer },
                };
            },
        }),
        assignLeverandorProcess: build.mutation<
            void,
            AssignLeverandorProcessRequest
        >({
            query(request) {
                return {
                    url: generateApiRoute([
                        leverandorProcessApiName,
                        assignProcessSegment,
                    ]),
                    method: 'POST',
                    body: request,
                };
            },
            invalidatesTags: ['LeverandorVurderingProcess'],
        }),
        getApprovedDocument: build.query<string, { prosessInstansId: string }>({
            query: ({ prosessInstansId }) => ({
                url: generateApiRoute([
                    leverandorProcessApiName,
                    approvedDocumentSegment,
                    prosessInstansId,
                    fileSegment,
                ]),
            }),
        }),
        getLeverandorProcessAttachments: build.query<
            Array<VedleggResponse>,
            { prosessInstansId: string }
        >({
            query: ({ prosessInstansId }) => ({
                url: generateApiRoute([
                    leverandorProcessApiName,
                    prosessInstansId,
                    vedleggSegment,
                ]),
            }),
            providesTags: ['LeverandorVedlegg'],
        }),
        getLeverandorProcessAttachmentContent: build.query<
            string,
            { vedleggId: string }
        >({
            query: ({ vedleggId }) => ({
                url: generateApiRoute([
                    leverandorProcessApiName,
                    vedleggSegment,
                    vedleggId,
                ]),
            }),
            providesTags: ['LeverandorVedlegg'],
        }),
        saveLeverandorProcessAttachment: build.mutation<
            void,
            {
                prosessInstansId: string;
                attachment: VedleggRequest;
            }
        >({
            query: ({ prosessInstansId, attachment }) => ({
                url: generateApiRoute([
                    leverandorProcessApiName,
                    prosessInstansId,
                    vedleggSegment,
                ]),
                method: 'POST',
                body: { attachment },
            }),
            invalidatesTags: ['LeverandorVedlegg'],
        }),
        deleteLeverandorProcessAttachment: build.mutation<
            void,
            { vedleggId: string }
        >({
            query: ({ vedleggId }) => ({
                url: generateApiRoute([
                    leverandorProcessApiName,
                    vedleggSegment,
                    vedleggId,
                ]),
                method: 'DELETE',
            }),
            invalidatesTags: ['LeverandorVedlegg'],
        }),
        getLeverandorProcessStatistics: build.query<
            LeverandorStatistics,
            {
                ansvarlig?: string;
                region?: string;
            }
        >({
            query: (args) => ({
                url: generateApiRoute([
                    leverandorProcessApiName,
                    statistikkSegment,
                ]),
                params: { ...args },
            }),
        }),
        getCompanyBySearch: build.query<
            Array<CompanySearchResponse>,
            { searchValue: string; country: COUNTRY_WITH_API_SUPPORT }
        >({
            query: ({ searchValue, country }) => ({
                url: generateApiRoute([leverandorApiName, company, search]),
                params: {
                    searchValue,
                    region: country,
                },
            }),
            providesTags: ['LeverandorCompanySearch'],
        }),
        getLeverandorInfoByOrgnummer: build.query<
            LeverandorInfo,
            { orgnummer: string }
        >({
            query: ({ orgnummer }) => ({
                url: generateApiRoute([leverandorApiName, superoffice]),
                params: { orgnummer },
            }),
        }),
        postBransjeRisiko: build.mutation<
            void,
            { bransjeId: string; risiko: number }
        >({
            query: ({ bransjeId, risiko }) => ({
                url: generateApiRoute([
                    leverandorApiName,
                    kodeverk,
                    bransje,
                    risikoApiName,
                ]),
                method: 'POST',
                body: { bransjeId: bransjeId, risiko: risiko },
            }),
            invalidatesTags: ['BransjeRisiko'],
        }),
        getForetakVurdering: build.query<
            ForetakResponse,
            ForetakRequest & {
                superofficeId?: string;
                forceLookup?: boolean;
            }
        >({
            query: ({
                orgNumber,
                countryCode,
                superofficeId,
                forceLookup = false,
            }) => {
                const countryCodeFallback = !countryCode ? 'NO' : countryCode;

                return {
                    url: generateApiRoute(
                        [
                            risikoVurderingApiName,
                            countryCodeFallback,
                            orgNumber,
                        ],
                        kdiApiName,
                    ),
                    method: 'GET',
                    headers: { 'Content-Type': 'text/plain' },
                    params: {
                        superofficeId,
                        forceLookup,
                    },
                };
            },
            providesTags: () => ['Risikovurdering'],
        }),
        getLandKodeverk: build.query<
            Record<string, string>,
            { locale?: string }
        >({
            query: ({ locale }) => ({
                url: generateApiRoute([leverandorApiName, kodeverk, land]),
                params: { locale },
            }),
        }),
    }),
});

const updateProcessWithFormfields = (
    process: LeverandorGodkjenningProsessResponse,
    formFields: Array<FormField>,
    activityId?: string,
) => {
    const updatedAktiviteter = process.aktiviteter?.map((a) => {
        return a.id && a.id === activityId
            ? {
                  ...a,
                  formFields: formFields,
              }
            : a;
    });

    return {
        ...process,
        aktiviteter: updatedAktiviteter,
        currentAktivitet: {
            ...process.currentAktivitet,
            formFields: formFields,
        },
    } as LeverandorGodkjenningProsessResponse;
};

export const updateLeverandorProcessFormfieldsCache = (
    prosessInstansId: string,
    culture: string,
    formfields: Array<FormField>,
    activityId?: string,
) =>
    leverandorApi.util.updateQueryData(
        'getLeverandorProcessByProcessId',
        { prosessInstansId: prosessInstansId, culture: culture },
        (prosjektProcess) =>
            updateProcessWithFormfields(
                prosjektProcess,
                formfields,
                activityId,
            ),
    );

export const {
    useSaveLeverandorProcessFormfieldsMutation,
    useExecuteLeverandorTaskMutation,
    useGetLeverandorVurderingQuery,
    useLazyGetLeverandorVurderingQuery,
    useGetLeverandorVurderingListQuery,
    useLazyGetLeverandorVurderingListQuery,
    useGetLeverandorRolesQuery,
    useGetUsersByLeverandorRoleAndRegionQuery,
    useLazyGetUsersByLeverandorRoleAndRegionQuery,
    useAssignLeverandorProcessMutation,
    useGetLeverandorConfluenceContentQuery,
    useDeleteLeverandorProcessMutation,
    useSendLeverandorReminderMutation,
    useGetLeverandorProcessListQuery,
    useGetLeverandorProcessByContactIdQuery,
    useLazyGetLeverandorProcessByContactIdQuery,
    useGetLeverandorProcessByProcessIdQuery,
    useLazyGetLeverandorProcessByProcessIdQuery,
    useCreateLeverandorProcessMutation,
    useGetLeverandorStatusQuery,
    useGetApprovedDocumentQuery,
    useGetLeverandorProcessAttachmentsQuery,
    useGetLeverandorProcessAttachmentContentQuery,
    useSaveLeverandorProcessAttachmentMutation,
    useDeleteLeverandorProcessAttachmentMutation,
    useGetLeverandorProcessStatisticsQuery,
    useGetLeverandorInfoByOrgnummerQuery,
    useGetCompanyBySearchQuery,
    useLazyGetLeverandorInfoByOrgnummerQuery,
    useGetLeverandorTypesQuery,
    useGetForetakVurderingQuery,
    useLazyGetForetakVurderingQuery,
    usePostBransjeRisikoMutation,
    useGetLandKodeverkQuery,
    useSendNotificationWithCommentMutation,
} = leverandorApi;
